import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronDown } from "lucide-react";
import { cn, Image, Input, type InputProps, Link, Select } from "@heroui/react";
import { ButtonOverride as Button } from "@/components/Buttons/ButtonOverride";
import type { TokenConfigType } from "@/constants/types/currency";
import { debounce } from "lodash";
import type { NativeTokenPrices } from "@/constants/project-specific/farms/types";
import { chainSettings, defaultNativeAddress, fallbackChainId, type ChainType } from "@/constants/chain-specific";
import type { RequiredAggregatorChainType } from "@/constants/project-specific";
import { formatBalance } from "@/util/balance/formatBalance";
import { SocialIcon } from "react-social-icons";
import type { SocialsConfigType } from "@/constants/project-specific/types";
import { AnimateNumber } from "motion-plus/react";
import { AnimatePresence, motion } from "motion/react";
interface TokenSwapInputProps extends InputProps {
  label?: string;
  onSelectClick?: () => void;
  selectedToken?: TokenConfigType | undefined;
  value?: string;
  balance?: number;
  /** Raw balance as bigint string for precision-safe max value selection */
  rawBalance?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Callback when user clicks max balance - provides raw bigint string for precision */
  onMaxClick?: (rawBalance: string) => void;
  nativePrices?: NativeTokenPrices;
  tokenPrices?: Record<string, number>;
  chainId: RequiredAggregatorChainType;
  readOnly?: boolean;
}
;
export default function TokenSwapInput({
  label = "Buy",
  onSelectClick,
  selectedToken,
  value,
  onChange,
  balance,
  rawBalance,
  onMaxClick,
  nativePrices,
  chainId,
  tokenPrices,
  readOnly = false,
  ...rest
}: TokenSwapInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Debounced onChange handler
  const debouncedOnChange = useCallback(debounce((event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event);
    }
  }, 300), [onChange]);
  const handleChange = (event_0: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event_0.target.value);
    debouncedOnChange(event_0);
  };
  const getSocialLinks = (socials: SocialsConfigType) => {
    const links: string[] = [];
    if (socials.website) {
      links.push(socials.website);
    }
    if (socials.other) {
      links.push(...socials.other.filter(url => url.includes("x.com") || url.includes("twitter.com") || url.includes("t.me") || url.includes("discord.com")));
    }
    return links;
  };
  return <div className={`cards-farm p-4 rounded-2xl border-2 transition-colors bg-slate-900/85 w-full ${isFocused ? "border-input" : "border-primary"}`}>
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-md font-medium">{label}</h2>
        <AnimatePresence mode="sync" initial={false}>
        {selectedToken?.socials && <div className="flex flex-row-reverse gap-x-1 px-1 min-w-[140px]">
            {getSocialLinks(selectedToken.socials).map((url_0, indexNum, arrayLength) => <motion.div layout layoutId={url_0} initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1,
            transition: {
              delay: 0.1 * (indexNum + 1),
              when: "afterChildren"
            }
          }} exit={{
            opacity: 0,
            scale: 0.8,
            transition: {
              delay: selectedToken?.socials && indexNum !== 0 ? 0.3 * getSocialLinks(selectedToken?.socials).length - indexNum : 0.15,
              when: "beforeChildren"
            }
          }} transition={{
            duration: 0.5,
            delay: 0.1 * (indexNum + 1),
            when: "afterChildren",
            ease: "easeInOut",
            type: "spring"
          }} key={url_0} className="opacity-50 hover:opacity-100 active:opacity-100 transition-opacity">
                <SocialIcon target="_blank" url={url_0} style={{
              height: 20,
              width: 20
            }} />
              </motion.div>)}
          </div>}
        </AnimatePresence>
      </div>
      <div className="flex flex-col space-y-2">
        <div className="flex space-x-2">
          <div className="flex-grow">
            <Input type="number" variant="flat" color="primary" placeholder="0" value={inputValue} onChange={handleChange} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)} isReadOnly={readOnly} className="text-xl w-full" classNames={{
            label: "text-black/50 dark:text-white/90",
            input: ["text-xl text-white/90", "placeholder:text-white/60"],
            innerWrapper: "bg-transparent",
            inputWrapper: ["bg-transparent", "bg-transparent", "backdrop-blur-xl", "hover:bg-transparent", "dark:hover:bg-transparent", "group-data-[focus=true]:bg-transparent", "dark:group-data-[focus=true]:bg-transparent", "!cursor-text"]
          }} {...rest} />
          </div>
          <div className="flex flex-col items-center min-w-[140px]">
            <Button className={cn("w-full ml-1 mr-1 shadow-xl p-0")} variant="shadow" color="default" size="md" startContent={selectedToken?.logo && <div className="flex items-stretch min-w-fit min-h-fit">
                    <Image src={selectedToken?.logo} className="object-contain" width={35} height={35} />
                  </div>} endContent={<ChevronDown className={cn("opacity-90 mr-1 h-5 w-5", !selectedToken?.symbol && "")} />} onPress={onSelectClick && onSelectClick}>
              {selectedToken?.symbol || <span className="pl-5 md:mr-3 mr-0">Select Token</span>}
            </Button>
          </div>
        </div>
        <div className="flex flex-row flex-nowrap w-full pt-2">
          <div className="flex align-bottom text-sm text-muted-foreground w-1/2">
            <AnimateNumber prefix="$" className="text-sm text-muted-foreground">
            {selectedToken?.address ? selectedToken.address.toLowerCase() === chainSettings[chainId ?? fallbackChainId].wNative?.address.toLowerCase() || selectedToken.address.toLowerCase() === defaultNativeAddress.toLowerCase() ? ((nativePrices?.[chainId]?.priceUsd ?? 0) * (Number(value) ?? 0)).toFixed(2) : tokenPrices ? (Number(tokenPrices?.[selectedToken.address.toLowerCase()] ?? 0) * Number(value)).toFixed(2) : "0.00" : "0.00"}
            </AnimateNumber>
          </div>
          <button type="button" className="flex justify-end text-right text-sm text-muted-foreground w-1/2" onClick={() => {
          const displayValue = balance?.toString() || "";
          setInputValue(displayValue);
          // For MAX click: notify parent with raw balance FIRST for precision-safe swaps
          // This must happen before onChange which would clear the raw value
          if (onMaxClick && rawBalance) {
            onMaxClick(rawBalance);
          }
          // Then update the display value in parent (without debounce to avoid race condition)
          if (onChange) {
            const event_1 = {
              target: {
                value: displayValue
              }
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(event_1);
          }
        }} onKeyUp={e => {
          if (e.key === "Enter" || e.key === " ") {
            const displayValue_0 = balance?.toString() || "";
            setInputValue(displayValue_0);
            // For MAX click: notify parent with raw balance FIRST for precision-safe swaps
            if (onMaxClick && rawBalance) {
              onMaxClick(rawBalance);
            }
            // Then update the display value in parent (without debounce)
            if (onChange) {
              const event_2 = {
                target: {
                  value: displayValue_0
                }
              } as React.ChangeEvent<HTMLInputElement>;
              onChange(event_2);
            }
          }
        }} tabIndex={0}>
            {selectedToken?.symbol && balance && balance > 0 && <AnimateNumber suffix={` ${selectedToken?.symbol}`} className="text-sm text-muted-foreground">{balance}</AnimateNumber>}
          </button>
        </div>
      </div>
    </div>;
}